import { useId, type ReactNode } from 'react';
import { useTheme } from '../context/ThemeContext';

interface UnavailablePanelProps {
  title: string;
  /** Why there is nothing to show (e.g. no data source yet). Plain text, no figures. */
  reason: string;
  /** Optional call to action, such as a link or button. */
  action?: ReactNode;
  /** Heading level of the title, so the panel fits the page outline. Defaults to 3. */
  headingLevel?: 2 | 3 | 4 | 5 | 6;
}

/**
 * Honest placeholder for a widget that has no data source. It never shows a number:
 * a missing value is explained, not replaced with a plausible one.
 */
export default function UnavailablePanel({ title, reason, action, headingLevel = 3 }: UnavailablePanelProps) {
  const { isDark } = useTheme();
  const headingId = useId();
  const Heading = `h${headingLevel}` as const;

  return (
    <section
      aria-labelledby={headingId}
      className={`rounded-xl border border-dashed p-5 flex items-start gap-3 ${
        isDark ? 'bg-white/[0.02] border-white/[0.12]' : 'bg-gray-50 border-gray-200'
      }`}
    >
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isDark ? 'bg-white/5' : 'bg-gray-100'
        }`}
      >
        <i aria-hidden="true" className="ri-information-line text-base text-gray-400" />
      </div>
      <div className="flex-1 min-w-0">
        <Heading id={headingId} className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
          {title}
        </Heading>
        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{reason}</p>
        {action && <div className="mt-3">{action}</div>}
      </div>
    </section>
  );
}
